'use client';
import { P, H4 } from '@osmosis/tailwind-ui/components/Text';
import { Button } from '@osmosis/tailwind-ui/components/Button';
import { Card } from '@osmosis/tailwind-ui/components/Card';
import {
  bg0,
  bgLightBlue,
  bgPrimaryLightest,
} from '@osmosis/tailwind-ui/styles/background';
import React, { Dispatch, SetStateAction, useState, ReactNode } from 'react';
import { useSearchParams } from 'next/navigation';

import InputValidation from '../../../components/InputValidation';
import { SubmitHandler, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useFetch } from '../../../context/FetchProvider';
import { FormattedMessage, useIntl } from 'react-intl';
import InputValidationMessage from '../../../components/InputValidationMessage';
import InputLabel from '../../../components/auth/InputLabel';
import { authMessages } from '../../../constants/authLocalization';
import { Route } from '../../../hooks/useRouterPush';
import ElsevierSignInExperienceHeader from '../../../components/learn/ElsevierSignInExperienceHeader';
import {
  ValidateEmail,
  ValidStatuses,
} from '@elsevier-health/osmosis-shared/types/auth';
import LoginFormFooter from '../../../components/auth/LoginFormFooter';
import { Flex } from '@osmosis/tailwind-ui/components/Box';
import useRouterPush from '../../../hooks/useRouterPush';
import { textBlue } from '@osmosis/tailwind-ui/styles/text';
import { cn } from '@osmosis/tailwind-ui/helpers/classes';

type Props = {
  email?: string;
  setShowPassNotice?: Dispatch<SetStateAction<boolean>>;
  setShowEmailForm?: Dispatch<SetStateAction<boolean>>;
  setUserEmail: Dispatch<SetStateAction<string>>;
  showSignUp?: boolean;
  inviteCode?: string;
};

type FormValues = { email: string };

const SupportLink = (value: ReactNode) => (
  <H4
    href="mailto:support@osmosis.org"
    className={cn('cursor-pointer no-underline', textBlue)}
  >
    {value}
  </H4>
);

/**
 * AuthMigrateLoginForm.
 */
const AuthMigrateLoginForm = (props: Props) => {
  const searchParams = useSearchParams();
  const afterAuthRedirect = searchParams?.get('afterAuthRedirect') ?? '/';
  const {
    email,
    setShowPassNotice,
    setShowEmailForm,
    setUserEmail,
    showSignUp = true,
    inviteCode = undefined,
  } = props;

  const [showInvalidCreateMsg, setShowInvalidCreateMsg] = useState(false);
  const [myError, setMyError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [unexpectedError, setUnexpectedError] = useState(false);
  const intl = useIntl();
  const routerPush = useRouterPush();

  const invalidLoginMsg = intl.formatMessage(authMessages.invalidLoginMsg);
  const requiredMsg = intl.formatMessage(authMessages.requiredMsg);
  const emailMsg = intl.formatMessage(authMessages.emailMsg);

  const unexpectedErrorMsg = intl.formatMessage(
    {
      defaultMessage: `Email validation failed with an unexpected error, please contact <link>support@osmosis.org</link> if the issue persists.`,
      id: 'emailValidationFailedMsg.error',
    },
    { link: (value: React.ReactNode) => SupportLink(value) },
  );

  const msgLink = (value: ReactNode, route: Route) => (
    <H4
      data-testid="in_here-button"
      href={route}
      className={cn('cursor-pointer no-underline', textBlue)}
    >
      {value}
    </H4>
  );

  const invalidMsg = intl.formatMessage(
    {
      defaultMessage: `The email you provided isn't recognized or is already active. Please <login>sign in</login> or <create>create an account</create>.`,
      id: 'partnerErrorMsg.error',
    },
    {
      login: (value: ReactNode) => msgLink(value, Route.LOGIN),
      create: (value: ReactNode) => msgLink(value, Route.CREATE),
    },
  );

  const validationSchema = yup
    .object({
      email: yup.string().email(emailMsg).required(requiredMsg),
    })
    .required(requiredMsg);

  const { post } = useFetch();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    mode: 'onSubmit',
    resolver: yupResolver(validationSchema),
  });

  const onSubmit: SubmitHandler<FormValues> = ({
    email,
  }: {
    email: string;
  }) => {
    setIsLoading(true);
    setShowInvalidCreateMsg(false);
    setMyError(false);
    setUserEmail(email);

    // Redirect to neologin signup directly if this is an invited user
    if (inviteCode) {
      window.location.href = `/neologin?connection=Signed-Up-Users&screen_hint=signup${
        email ? `&login_hint=${encodeURIComponent(email)}` : ''
      }${
        '&afterAuthRedirect=' +
        encodeURIComponent('/start?invite_code=' + inviteCode)
      }`;

      return;
    }

    post<
      ValidateEmail,
      { email: string; afterAuthRedirect: string | null | undefined }
    >('/validateEmail', {
      email,
      afterAuthRedirect,
    })
      .then((res) => {
        setIsLoading(false);
        if ([ValidStatuses.invalidEmail].includes(res.data.status)) {
          setMyError(true);
        } else if (res.data.passwordLoginRequired) {
          setShowPassNotice?.(res.data.tempSent);
          setShowEmailForm?.(false);
        } else if (
          [
            ValidStatuses.SSO,
            ValidStatuses.neoid,
            ValidStatuses.evolveUser,
          ].includes(res.data.status) &&
          res.data.redirectURL
        ) {
          window.location.href = res.data.redirectURL;
          return;
        } else {
          setShowPassNotice?.(res.data.tempSent);
          setShowEmailForm?.(true);
        }
      })
      .catch(() => {
        setMyError(true);
        setUnexpectedError(true);
        setIsLoading(false);
      });
  };

  const emailErrorMsg = errors?.email?.message?.toString();

  const signUpAction = () => {
    routerPush(Route.CREATE);
  };

  return (
    <Card className="w-full max-w-xl p-0">
      <ElsevierSignInExperienceHeader
        containerStyle={{ backgroundColor: `${bgPrimaryLightest}` }}
      />

      <Card className="border-0 p-8">
        <form onSubmit={handleSubmit(onSubmit)}>
          <InputLabel
            content={
              <FormattedMessage id="login.email" defaultMessage="Email" />
            }
            htmlFor="email"
            hasError={!!emailErrorMsg}
          />

          <InputValidation
            showErrorMessage={false}
            error={!!emailErrorMsg}
            variant="filled"
            inputProps={{
              ...register('email', {
                onChange: () => setShowInvalidCreateMsg(false),
                value: email ? email : '',
              }),
              id: 'email',
              placeholder: intl.formatMessage({
                id: 'login.email_placeholder',
                defaultMessage: 'Enter email (School email preferred)',
              }),
              ['data-testid']: 'email-input',
              autoComplete: 'section-auth-migrate-login email',
            }}
            containerProps={{
              cn: 'mb-3',
            }}
          />

          <Button
            size="l"
            variant="outline"
            type="submit"
            disabled={isLoading}
            isLoading={isLoading}
            loadingSpinnerProps={{ size: 20, color: 'textInvert' }}
            data-testid="authMigrateLoginForm.next-btn"
            className={`w-full ${bg0} rounded-full ${textBlue} hover:${bgLightBlue} hover:border-blue-100`}
          >
            <FormattedMessage id="common.next" defaultMessage="Next" />
          </Button>
          <LoginFormFooter isSignUp={false} />

          {showSignUp && (
            <Flex className="my-4 justify-center gap-2">
              <P className="!text5 opacity-0.75 inline-block !font-bold">
                <FormattedMessage
                  id="login.account"
                  defaultMessage="Don't have an account?"
                />
              </P>
              <P
                data-testid="sign_up-button"
                style={{
                  textDecoration: 'none',
                }}
                onClick={signUpAction}
                onKeyDown={(e) => e.key === 'Enter' && signUpAction()}
                tabIndex={0}
                className={`!${textBlue} cursor-pointer !font-bold `}
              >
                <FormattedMessage
                  id="login.sign_up"
                  defaultMessage=" Sign up"
                />
              </P>
            </Flex>
          )}

          <InputValidationMessage
            error={showInvalidCreateMsg}
            errorMessage={invalidLoginMsg}
            messageProps={{ 'data-testid': 'invalid-login-msg' }}
          />

          <InputValidationMessage
            error={!showInvalidCreateMsg && !!emailErrorMsg}
            errorMessage={emailErrorMsg}
          />

          <InputValidationMessage
            error={myError}
            errorMessage={(() => {
              if (unexpectedError) return unexpectedErrorMsg;
              return invalidMsg;
            })()}
            messageProps={{
              'data-testid': 'invalid-msg',
            }}
          />
        </form>
      </Card>
    </Card>
  );
};

export default AuthMigrateLoginForm;
